import { useState, useEffect } from "react"
import { UserSearch } from "@/components/UserSearch"
import { WeekCalendar } from "@/components/WeekCalendar"
import { UserProfileCard } from "@/components/user-profile-card"
import { GenerateSeasonButton } from "@/components/GenerateSeasonButton"
import { useWorkoutStore } from "@/stores/workoutStore"
import { getUserProfile } from "@/lib/api"
import { useReadOnly } from "@/lib/read-only"

export function EditorPage() {
  const { readOnly } = useReadOnly()
  const {
    currentUser,
    season,
    isLoading,
    extendSeason,
    isExtending,
    loadUserSeason,
    isDirty,
    isSaving,
    saveChanges,
    discardChanges,
  } = useWorkoutStore()
  const [editorUserProfile, setEditorUserProfile] = useState<Record<
    string,
    unknown
  > | null>(null)

  useEffect(() => {
    if (!currentUser?.id) {
      setEditorUserProfile(null)
      return
    }
    getUserProfile(currentUser.id)
      .then(setEditorUserProfile)
      .catch(() => setEditorUserProfile(null))
  }, [currentUser?.id])

  // Browser-level guard against closing/reloading the tab with unsaved edits.
  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  const handleExtend = () => {
    if (isDirty) {
      const ok = window.confirm(
        'You have unsaved edits. Extending will discard them. Continue?',
      )
      if (!ok) return
    }
    extendSeason()
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <UserSearch />
      </div>

      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          <p className="mt-4 text-muted-foreground">Loading workout plan...</p>
        </div>
      )}

      {!isLoading && currentUser && season && (
        <div>
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold flex items-center gap-2">
                {currentUser.first_name} {currentUser.last_name}'s Workout Plan
                {isDirty && (
                  <span
                    title="You have unsaved edits"
                    className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                  >
                    <span className="size-1.5 rounded-full bg-amber-500" />
                    Unsaved changes
                  </span>
                )}
              </h2>
              {season.goal && (
                <p className="text-muted-foreground mt-1">
                  Goal: {season.goal}
                </p>
              )}
            </div>
            <div className="shrink-0 flex items-center gap-2">
              <button
                onClick={discardChanges}
                disabled={readOnly || !isDirty || isSaving}
                title={
                  readOnly
                    ? "Read-only mode active"
                    : !isDirty
                      ? "No unsaved edits"
                      : "Discard local edits and reload from the last saved version"
                }
                className="px-3 py-1.5 text-sm border border-input bg-background rounded-md hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Refresh
              </button>
              <button
                onClick={saveChanges}
                disabled={readOnly || !isDirty || isSaving}
                title={
                  readOnly
                    ? "Read-only mode active"
                    : !isDirty
                      ? "No edits to save"
                      : "Push edits to the user"
                }
                className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving ? "Saving..." : "Save workouts"}
              </button>
              <button
                onClick={handleExtend}
                disabled={readOnly || isExtending || !season}
                title={
                  readOnly
                    ? "Read-only mode active"
                    : !season
                      ? "Generate the user's first season before extending"
                      : "Extend this user's workout plan by one week"
                }
                className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isExtending ? "Extending..." : "Extend Plan"}
              </button>
            </div>
          </div>
          {editorUserProfile && (
            <UserProfileCard profile={editorUserProfile} className="mb-4" />
          )}
          <WeekCalendar />
        </div>
      )}

      {!isLoading && currentUser && !season && (
        <div className="text-center py-12 bg-card rounded-lg shadow-sm space-y-4">
          <p className="text-muted-foreground">
            No workout plan found for {currentUser.first_name}{" "}
            {currentUser.last_name}
          </p>
          <GenerateSeasonButton
            userId={currentUser.id}
            userLabel={`${currentUser.first_name} ${currentUser.last_name}`}
            onGenerated={async () => {
              await loadUserSeason(currentUser.id)
            }}
          />
        </div>
      )}

      {!isLoading && !currentUser && (
        <div className="text-center py-12 text-muted-foreground">
          Search for a user to view and edit their workout plan
        </div>
      )}
    </div>
  )
}
